// SHARE-3: reusable directory typeahead. ShareDialog uses this to pick the platform user a
// resource is shared with (SHARE-6 reuses it for speaker resolution). Debounces `GET /users?q=`
// via `useUserSearch`, renders a listbox following the AppShell (command palette) search's
// a11y/copy conventions, and supports full keyboard navigation.
//
// SHARE-7 (docs/specs/share-7.md §5.3): `externalSource`/`onSelectExternal`/`onRequestConsent`/
// `excludeOids` are optional and opt-in — when `externalSource` is absent, behavior is
// byte-identical to what SHARE-3 shipped (SHARE-6's speaker-resolution usage needs zero
// changes — including not requiring a `QueryClientProvider` in the tree just because this
// component exists: the second query's `useQuery` call lives in `<ExternalUserSearch>` below,
// which is only ever mounted when `externalSource` is actually supplied). When present, a
// second query runs alongside the platform search (a browser-side Microsoft Graph people
// search in real Entra mode, a mock fixture in `dev:mock`), merged into the same listbox:
// platform rows first, then external rows whose `entra_oid` matches no platform row's (the
// platform row already has a shareable `users.id` — no provisioning hop needed), minus
// `excludeOids`.
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUserSearch } from '../lib/queries';
import { GraphConsentRequiredError } from '../lib/graphDirectory';
import type { ExternalDirectorySource } from '../lib/graphDirectory';
import { Avatar } from './ui';
import type { ProvisionUserRequest, UserSummary } from '@scribe/core/types';

/** No debounce util exists anywhere in the repo today (verified via grep before adding this) —
 * kept local to the one component that needs it. */
function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

// Stable default so callers that omit `excludeIds`/`excludeOids` don't hand this component a
// fresh array identity on every render — see the `results`/active-index effect below, which
// would otherwise reset the keyboard-highlighted option back to 0 on every keystroke.
const EMPTY_IDS: string[] = [];

type PickerRow =
  | { kind: 'platform'; user: UserSummary }
  | { kind: 'external'; person: ProvisionUserRequest };

function rowKey(row: PickerRow): string {
  return row.kind === 'platform' ? `platform:${row.user.id}` : `external:${row.person.entra_oid}`;
}

function rowName(row: PickerRow): string {
  return row.kind === 'platform' ? row.user.display_name : row.person.display_name;
}

function rowEmail(row: PickerRow): string {
  return row.kind === 'platform' ? row.user.email : row.person.email;
}

interface ExternalSearchState {
  data: ProvisionUserRequest[] | undefined;
  error: Error | null;
  refetch: () => void;
}

const EMPTY_EXTERNAL_STATE: ExternalSearchState = { data: undefined, error: null, refetch: () => {} };

/**
 * Runs the second (Graph-backed) query and reports its state up to `UserPicker` via `onChange`
 * — kept as its OWN component (rather than a `useQuery` call directly inside `UserPicker`) so
 * the hook is only ever invoked while `externalSource` is actually supplied. `UserPicker` mounts
 * this conditionally; when it's unmounted, no `useQuery`/`QueryClientProvider` requirement
 * leaks onto callers (SHARE-6's speaker resolution) that never pass `externalSource`.
 */
function ExternalUserSearch({
  source,
  query,
  onChange,
}: {
  source: ExternalDirectorySource;
  query: string;
  onChange: (state: ExternalSearchState) => void;
}) {
  const result = useQuery<ProvisionUserRequest[], Error>({
    queryKey: ['users', 'external-search', query],
    queryFn: () => source(query),
    enabled: query.length >= 2,
    staleTime: 30_000,
    retry: false, // a consent error must surface immediately, not after 3 tries
  });

  useEffect(() => {
    onChange({ data: result.data, error: result.error ?? null, refetch: () => result.refetch() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data, result.error]);

  return null;
}

export function UserPicker({
  onSelect,
  onSelectExternal,
  onRequestConsent,
  externalSource,
  excludeIds = EMPTY_IDS,
  excludeOids = EMPTY_IDS,
  placeholder = 'Search people by name or email…',
  ariaLabel = 'Search people',
}: {
  onSelect: (user: UserSummary) => void;
  onSelectExternal?: ((person: ProvisionUserRequest) => void) | undefined;
  onRequestConsent?: (() => Promise<void>) | undefined;
  externalSource?: ExternalDirectorySource | undefined;
  excludeIds?: string[];
  excludeOids?: string[];
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const debouncedQuery = useDebouncedValue(query, 250);
  const trimmedQuery = query.trim();
  const trimmedDebounced = debouncedQuery.trim();
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();

  const search = useUserSearch(debouncedQuery);

  const excludeSet = useMemo(() => new Set(excludeIds), [excludeIds]);
  const results = useMemo(
    () => (search.data?.items ?? []).filter((u) => !excludeSet.has(u.id)),
    [search.data, excludeSet],
  );

  // Second (external) source's query state, reported up from `<ExternalUserSearch>` (mounted
  // below, only when `externalSource` is supplied) — see that component's doc comment.
  const [externalState, setExternalState] = useState<ExternalSearchState>(EMPTY_EXTERNAL_STATE);

  const excludeOidSet = useMemo(() => new Set(excludeOids), [excludeOids]);
  const platformOids = useMemo(() => new Set(results.map((u) => u.entra_oid)), [results]);
  const externalRows = useMemo(() => {
    if (!externalSource) return [];
    return (externalState.data ?? []).filter(
      (p) => !platformOids.has(p.entra_oid) && !excludeOidSet.has(p.entra_oid),
    );
  }, [externalSource, externalState.data, platformOids, excludeOidSet]);

  // Platform rows first, then external rows — capped at 20 rendered options combined. With no
  // `externalSource`, `externalRows` is always [] and this is exactly SHARE-3's `results` list.
  const combinedRows: PickerRow[] = useMemo(
    () => [
      ...results.map((u): PickerRow => ({ kind: 'platform', user: u })),
      ...externalRows.map((p): PickerRow => ({ kind: 'external', person: p })),
    ].slice(0, 20),
    [results, externalRows],
  );

  // Keep the highlighted option in bounds whenever a new search result set arrives. Keyed on
  // the raw query data (react-query keeps these references stable unless the underlying data
  // actually changed) rather than the derived `combinedRows` array, which would get a fresh
  // identity on every render if a caller passes unstable `excludeIds`/`excludeOids` — that
  // would silently reset the keyboard-highlighted option back to 0 on every keystroke.
  useEffect(() => {
    setActiveIndex(combinedRows.length > 0 ? 0 : -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.data, externalState.data]);

  const isOpen = query.length > 0;

  function selectRow(row: PickerRow) {
    if (row.kind === 'platform') onSelect(row.user);
    else onSelectExternal?.(row.person);
    setQuery('');
    setActiveIndex(-1);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setActiveIndex(-1);
      return;
    }
    if (!isOpen || combinedRows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % combinedRows.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + combinedRows.length) % combinedRows.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = combinedRows[activeIndex];
      if (row) selectRow(row);
    }
  }

  const activeOptionId =
    activeIndex >= 0 && combinedRows[activeIndex] ? `${listboxId}-option-${activeIndex}` : undefined;

  const externalError = externalSource ? externalState.error : null;
  const consentRequired = externalError instanceof GraphConsentRequiredError;

  return (
    <div className="user-picker">
      {externalSource && (
        <ExternalUserSearch source={externalSource} query={trimmedDebounced} onChange={setExternalState} />
      )}
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeOptionId}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
      />
      {isOpen && (
        <div className="user-picker-panel" id={listboxId} role="listbox" aria-label={ariaLabel}>
          {trimmedQuery.length < 2 ? (
            <p className="user-picker-hint">Type at least 2 characters</p>
          ) : trimmedDebounced.length < 2 || search.isLoading ? (
            <p className="user-picker-hint">Searching…</p>
          ) : search.isError ? (
            <p className="user-picker-hint">Search failed. Try again.</p>
          ) : combinedRows.length === 0 ? (
            <p className="user-picker-hint">No people found.</p>
          ) : (
            <ul className="user-picker-list">
              {combinedRows.map((row, i) => (
                <li key={rowKey(row)}>
                  <button
                    type="button"
                    id={`${listboxId}-option-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    className={'user-picker-item' + (i === activeIndex ? ' is-active' : '')}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => selectRow(row)}
                  >
                    <Avatar name={rowName(row)} size="sm" />
                    <span className="user-picker-item-text">
                      <span className="user-picker-item-name">{rowName(row)}</span>
                      <span className="muted user-picker-item-email">{rowEmail(row)}</span>
                      {row.kind === 'external' && (
                        <span className="muted user-picker-item-hint">Hasn&apos;t signed in yet</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {externalSource && consentRequired && (
            <button
              type="button"
              className="user-picker-consent"
              onClick={() => {
                void onRequestConsent?.().then(() => externalState.refetch());
              }}
            >
              Search your whole organization
            </button>
          )}
          {externalSource && externalError && !consentRequired && (
            <p className="muted user-picker-hint">Org-wide search unavailable</p>
          )}
        </div>
      )}
    </div>
  );
}
